'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useQueryState } from 'nuqs'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import type { ProfileType } from '@/types/profile-type'
import { logout } from '@/actions/auth/logout'
import { getProfileAction } from '@/actions/user/get-profile-action'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import { useAuthStatus } from '@/hooks/use-auth-status'
import { useOrder } from '@/hooks/use-order-store'
import { bff_client } from '@/lib/apiClient'
import { getDeviceInfo } from '@/utils/get-device-info'
import { getInitials } from '@/utils/get-initials'

export function useNavbar() {
  const router = useRouter()
  const pathname = usePathname()
  const status = useAuthStatus()
  const { setOrder, order } = useOrder()

  const [isMounted, setIsMounted] = useState(false)
  const [referrer, setReferrer] = useState('')
  const [profile, setProfile] = useState<ProfileType | null>(null)

  const [toggleMenu, setToggleMenu] = useState(false)
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [termSearch, setTermSearch] = useState('')
  const [searchQuery, setSearchQuery] = useQueryState('q', {
    defaultValue: '',
    clearOnDefault: true,
  })

  const isLogged = isMounted && status === 'authenticated'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Fetch profile when user is authenticated
  useEffect(() => {
    async function fetchProfile() {
      if (isLogged && !profile) {
        try {
          const result = await getProfileAction()
          if ('data' in result && result.data) {
            setProfile(result.data as ProfileType)
          } else {
            setProfile(null)
          }
        } catch {
          // Silently handle error - profile will remain null
        }
      }
    }

    fetchProfile()
  }, [isLogged, profile])

  useEffect(() => {
    if (isMounted && searchQuery && window.location.pathname !== '/cursos') {
      setIsSearchVisible(true)
    }
  }, [searchQuery, isMounted])

  useEffect(() => {
    if (pathname) {
      setIsSearchVisible(false)
    }
  }, [pathname])

  useEffect(() => {
    async function fetchOrder() {
      try {
        const response = await bff_client
          .get('order', { timeout: 30_000, cache: 'no-store' })
          .json()

        setOrder((response as { object: any }).object)
      } catch {
        // Silently handle error - order will remain null
      }
    }

    if (isLogged && !order) {
      fetchOrder()
    }
  }, [setOrder, isLogged, order])

  function handleToggleMenu() {
    setToggleMenu(state => !state)
    handleBlur()
  }

  function handleFocus() {
    setIsSearchVisible(true)
  }

  useEffect(() => {
    setReferrer(document.referrer)
  }, [])

  const device = useMemo(() => getDeviceInfo(), [])

  function triggerGtmEvent() {
    // if (!isMounted || typeof window === 'undefined') return

    const eventData = {
      event: 'vitrine',
      eventName: 'search',
      customDataObject: {
        environment: 'Vitrine',
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: profile?.reference ?? profile?.external_id,
          item_name: profile?.full_name,
          item_email: profile?.email,
        }),
      },
    }

    trackEvent(eventData)
  }

  function handleBlur() {
    setIsSearchVisible(false)
  }

  function handleToggleSearchMobile() {
    setIsSearchVisible(state => !state)
  }

  function handleRedirectCourse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    router.push(`/cursos?q=${searchQuery}`)
    setIsSearchVisible(false)
  }

  async function handleLogout() {
    try {
      await logout()
      setProfile(null)
      router.push('/')
    } catch {
      // Silently handle error
    }
  }

  // Handle empty string case - nullish coalescing doesn't catch empty strings
  const fullName = profile?.full_name?.trim() || 'User Created'
  const nameInitials = getInitials(fullName)

  return {
    isLogged,
    profile,
    toggleMenu,
    isSearchVisible,
    termSearch,
    searchQuery,
    nameInitials,
    handleToggleMenu,
    handleFocus,
    handleBlur,
    handleToggleSearchMobile,
    handleRedirectCourse,
    handleLogout,
    setTermSearch,
    setSearchQuery,
    triggerGtmEvent,
  }
}
